"use client";

import { useEffect, useRef } from "react";
import { useSession } from "next-auth/react";

import { useSubscription } from "@/app/providers/SubscriptionProvider";
import { normalizeTier } from "@/lib/pricing";
import classNames from "@/utils/class-names";

// Shows a "Partner" pill next to the user's profile picture for
// authenticated affiliate-tier users (affiliates pay the partner price;
// see src/lib/pricing.ts). Hydrates the subscription on mount when it
// hasn't been loaded yet, mirroring subscription-loader. Renders nothing
// for guests, associates, or while logged out.
export default function PartnerBadge({ className }: { className?: string }) {
  const { data: session, status } = useSession();
  const { state, readSubscription, dispatch } = useSubscription();

  // Fetch the subscription at most once per mount. A naive effect that
  // refetches on `state.subscription === null` loops forever: the read can
  // resolve to null, and set-subscription always produces a fresh state
  // object, which re-renders and re-fires the effect. The ref latches it.
  const requested = useRef(false);

  useEffect(() => {
    if (status !== "authenticated") return;
    if (state.subscription !== null) return;
    if (requested.current) return;

    requested.current = true;
    // readSubscription/dispatch are intentionally not in the deps: the ref
    // guard makes their (unstable) identity irrelevant and avoids the loop.
    void readSubscription().then((subscription) => {
      dispatch({ type: "set-subscription", subscription });
    });
  }, [status, state.subscription]);

  const isPartner =
    status === "authenticated" &&
    normalizeTier({ session, subscription: state.subscription }) ===
      "affiliate";

  if (!isPartner) return null;

  return (
    <span
      data-testid="partner-badge"
      className={classNames(
        "inline-flex items-center rounded-full bg-rich-brown/10 px-2 py-0.5 text-xs font-semibold tracking-wide text-rich-brown",
        className ?? "",
      )}
    >
      Partner
    </span>
  );
}
